import { Input, RadioGroup, RadioGroupItem } from '@agavi/ui';
import { ChevronLeft } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { SignupFormValues } from '@/types/auth';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';


type StepTwoProps = {
  onBack: () => void;
  onSubmit: (e: React.FormEvent) => void;
  apiError?: string;
  loading?: boolean;
};

export default function StepTwo({ onBack, onSubmit, apiError, loading }: StepTwoProps) {
  const { t } = useTranslation();
  const form = useFormContext<SignupFormValues>();

  return (
    <Form {...form}>
      <form
        onSubmit={onSubmit}
        className='w-full max-w-[320px] space-y-8'
      >
        <button
          type='button'
          onClick={onBack}
          className='flex items-center gap-1 text-sm font-medium text-ws-text hover:underline'
        >
          <ChevronLeft className='w-4 h-4' />
          {t('common.back')}
        </button>

        <div className='text-center space-y-2'>
          <p className='text-sm text-ws-muted'>{t('auth.signup.step2.stepLabel')}</p>
          <h2 className='text-3xl font-semibold text-ws-text'>{t('auth.signup.step2.title')}</h2>
          <p className='text-base text-ws-muted'>
            {t('auth.signup.step2.subtitle')}
          </p>
        </div>

        <div className='space-y-6'>
        <FormField
          control={form.control}
          name='password'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-base font-medium'>{t('auth.signup.step2.password')}</FormLabel>
              <FormControl>
                <Input
                  type='password'
                  placeholder={t('auth.signup.step2.passwordPlaceholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
              <p className='text-xs text-ws-muted'>
                {t('auth.signup.step2.passwordHint')}
              </p>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='confirmPassword'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-base font-medium'>{t('auth.signup.step2.confirmPassword')}</FormLabel>
              <FormControl>
                <Input
                  type='password'
                  placeholder={t('auth.signup.step2.confirmPasswordPlaceholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
              <p className='text-xs text-ws-muted'>
                {t('auth.signup.step2.confirmPasswordHint')}
              </p>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='role'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-base font-medium'>{t('auth.signup.step2.role')}</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className='flex gap-4'
                >
                  <FormItem className='flex items-center gap-2 space-y-0'>
                    <FormControl>
                      <RadioGroupItem value='teacher' />
                    </FormControl>
                    <FormLabel className='font-normal'>{t('auth.signup.step2.roleTeacher')}</FormLabel>
                  </FormItem>
                  <FormItem className='flex items-center gap-2 space-y-0'>
                    <FormControl>
                      <RadioGroupItem value='student' />
                    </FormControl>
                    <FormLabel className='font-normal'>{t('auth.signup.step2.roleStudent')}</FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
              <p className='text-xs text-ws-muted'>
                {t('auth.signup.step2.roleHint')}
              </p>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='acceptTerms'
          render={({ field }) => (
            <FormItem className='flex items-start gap-2 space-y-0'>
              <FormControl>
                <input
                  type='checkbox'
                  className='mt-1'
                  checked={field.value === true}
                  onChange={(e) =>
                    field.onChange(e.target.checked ? true : undefined)
                  }
                />
              </FormControl>
              <div className='space-y-1'>
                <FormLabel className='text-sm font-medium'>
                  {t('auth.signup.step2.terms')}
                </FormLabel>
                <p className='text-sm text-ws-muted'>
                  {t('auth.signup.step2.termsText')}
                </p>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        </div>

        {apiError && <p className='text-sm text-ws-error-fg text-center'>{apiError}</p>}

        <Button type='submit' variant='primary' size='lg' block busy={loading}>
          {loading ? t('auth.signup.step2.submitting') : t('auth.signup.step2.submit')}
        </Button>
      </form>
    </Form>
  );
}
